import { Permission, getUsersWithPermission, getUsersWithAnyPermission } from '@shared/permissions';

// Types for menu items
export interface BaseMenuItem {
  /** 
   * Required permissions for this menu item.
   * User must have at least ONE of these permissions to see the item.
   */
  requiredPermissions?: Permission[];
  /**
   * @deprecated Use `requiredPermissions` instead.
   * Legacy whitelist - kept for backwards compatibility during migration.
   */
  whitelist?: string[];
}

export interface ActionMenuItem extends BaseMenuItem {
  type: 'action';
  label: string;
  action: () => void;
  associatedPath?: string; // Optional: for direct path matching if needed
}

export interface SeparatorMenuItem extends BaseMenuItem {
  type: 'separator';
}

export type MenuItem = ActionMenuItem | SeparatorMenuItem;

export interface MenuCategory {
  trigger: string;
  items: MenuItem[];
  activePathPrefixes: string[]; // New property
}

export interface ActionHandlers {
  handleNavigation: (url: string) => void;
  toggleMailDrawer: () => void;
  toggleSettingsCenter: () => void;
  onLogout: () => void;
  handleClearCache: () => void;
}

/**
 * Helper to get whitelist from permissions for backwards compatibility.
 * This allows the menu system to work with both old and new patterns.
 */
const getWhitelistFromPermissions = (permissions: Permission[]): string[] => {
  return getUsersWithAnyPermission(permissions);
};

export const getMenuItems = (
  actionHandlers: ActionHandlers
): MenuCategory[] => {
  const { handleNavigation, toggleMailDrawer, toggleSettingsCenter, onLogout, handleClearCache } = actionHandlers;

  const salesMenu: MenuCategory = {
    trigger: 'Sales',
    items: [
      { 
        type: 'action', 
        label: 'Customers', 
        action: () => handleNavigation('/customers'), 
        associatedPath: '/customers', 
        requiredPermissions: ['customers.view'],
        whitelist: getWhitelistFromPermissions(['customers.view']),
      },
      { 
        type: 'separator', 
        requiredPermissions: ['orders.view'],
        whitelist: getWhitelistFromPermissions(['orders.view']),
      },
      { 
        type: 'action', 
        label: 'Orders', 
        action: () => handleNavigation('/orders'), 
        associatedPath: '/orders', 
        requiredPermissions: ['orders.view'],
        whitelist: getWhitelistFromPermissions(['orders.view']),
      },
      { 
        type: 'action', 
        label: 'Shipments', 
        action: () => handleNavigation('/shipments'), 
        associatedPath: '/shipments', 
        requiredPermissions: ['shipments.view'],
        whitelist: getWhitelistFromPermissions(['shipments.view']),
      },
      { 
        type: 'separator', 
        requiredPermissions: ['customers.pricing'],
        whitelist: getWhitelistFromPermissions(['customers.pricing']),
      },
      { 
        type: 'action', 
        label: 'Edit Pricing', 
        action: () => handleNavigation('/pricing/edit'), 
        associatedPath: '/pricing/edit', 
        requiredPermissions: ['customers.pricing'],
        whitelist: getWhitelistFromPermissions(['customers.pricing']),
      },
    ],
    activePathPrefixes: ['/customers', '/pricing', '/orders'],
  };
  
  const productDevelopmentMenu: MenuCategory = {
    trigger: 'Product',
    items: [
      { 
        type: 'action', 
        label: 'Parts', 
        action: () => handleNavigation('/pricing'), 
        associatedPath: '/pricing', 
        requiredPermissions: ['parts.view'],
        whitelist: getWhitelistFromPermissions(['parts.view']),
      },
      { 
        type: 'separator', 
        requiredPermissions: ['parts.dev-queue'],
        whitelist: getWhitelistFromPermissions(['parts.dev-queue']),
      },
      { 
        type: 'action', 
        label: 'Dev. Queue', 
        action: () => handleNavigation('/pd/queue'), 
        associatedPath: '/pd/queue', 
        requiredPermissions: ['parts.dev-queue'],
        whitelist: getWhitelistFromPermissions(['parts.dev-queue']),
      },
      { 
        type: 'action', 
        label: 'Part Terms', 
        action: () => handleNavigation('/pd/part-terms'), 
        associatedPath: '/pd/part-terms', 
        requiredPermissions: ['parts.terms'],
        whitelist: getWhitelistFromPermissions(['parts.terms']),
      },
      { 
        type: 'separator', 
        requiredPermissions: ['admin.utilities', 'parts.pd-tools'],
        whitelist: getWhitelistFromPermissions(['admin.utilities', 'parts.pd-tools']),
      },
      { 
        type: 'action', 
        label: 'Density Calculator', 
        action: () => handleNavigation('/pd/density-calculator'), 
        associatedPath: '/pd/density-calculator', 
        requiredPermissions: ['admin.utilities', 'parts.pd-tools'],
        whitelist: getWhitelistFromPermissions(['admin.utilities', 'parts.pd-tools']),
      },
      { 
        type: 'action', 
        label: 'NC File Analyzer', 
        action: () => handleNavigation('/pd/nc-file-analyzer'), 
        associatedPath: '/pd/nc-file-analyzer', 
        requiredPermissions: ['admin.utilities', 'parts.pd-tools'],
        whitelist: getWhitelistFromPermissions(['admin.utilities', 'parts.pd-tools']),
      },
    ],
    activePathPrefixes: ['/pd'],
  };
  
  const materialsMenu: MenuCategory = {
    trigger: 'Purchasing',
    items: [
      { 
        type: 'action', 
        label: 'Inventory', 
        action: () => handleNavigation('/inventory'), 
        associatedPath: '/inventory', 
        requiredPermissions: ['inventory.view'],
        whitelist: getWhitelistFromPermissions(['inventory.view']),
      },
      { 
        type: 'separator', 
        requiredPermissions: ['purchasing.view'],
        whitelist: getWhitelistFromPermissions(['purchasing.view']),
      },
      { 
        type: 'action', 
        label: 'Vendors', 
        action: () => handleNavigation('/vendors'), 
        associatedPath: '/vendors', 
        requiredPermissions: ['purchasing.vendors'],
        whitelist: getWhitelistFromPermissions(['purchasing.vendors']),
      },
      { 
        type: 'action', 
        label: 'Purchase Orders', 
        action: () => handleNavigation('/purchase-orders'), 
        associatedPath: '/purchase-orders', 
        requiredPermissions: ['purchasing.view'],
        whitelist: getWhitelistFromPermissions(['purchasing.view']),
      },
      { 
        type: 'action', 
        label: 'Receivers', 
        action: () => handleNavigation('/receivers'), 
        associatedPath: '/receivers', 
        requiredPermissions: ['purchasing.receivers'],
        whitelist: getWhitelistFromPermissions(['purchasing.receivers']),
      },
    ],
    activePathPrefixes: ['/inventory', '/vendors', '/purchase-orders', '/receivers'],
  };
  
  const productionMenu: MenuCategory = {
    trigger: 'Schedule',
    items: [
      { 
        type: 'action', 
        label: 'Week View', 
        action: () => handleNavigation(`/schedule/week`), 
        associatedPath: '/schedule', 
        requiredPermissions: ['schedule.view'],
        whitelist: getWhitelistFromPermissions(['schedule.view']),
      },
      { 
        type: 'action', 
        label: 'Customer View', 
        action: () => handleNavigation(`/schedule/customer`), 
        associatedPath: '/schedule', 
        requiredPermissions: ['schedule.view'],
        whitelist: getWhitelistFromPermissions(['schedule.view']),
      },
      { 
        type: 'action', 
        label: 'Up Next', 
        action: () => handleNavigation(`/schedule/up-next`), 
        associatedPath: '/schedule', 
        requiredPermissions: ['schedule.view'],
        whitelist: getWhitelistFromPermissions(['schedule.view']),
      },
      { 
        type: 'action', 
        label: 'Job Requirements', 
        action: () => handleNavigation(`/schedule/requirements`), 
        associatedPath: '/schedule', 
        requiredPermissions: ['schedule.view'],
        whitelist: getWhitelistFromPermissions(['schedule.view']),
      },
      { 
        type: 'separator', 
        requiredPermissions: ['router.view'],
        whitelist: getWhitelistFromPermissions(['router.view']),
      },
      { 
        type: 'action', 
        label: 'Router', 
        action: () => handleNavigation('/router'), 
        associatedPath: '/router', 
        requiredPermissions: ['router.view'],
        whitelist: getWhitelistFromPermissions(['router.view']),
      },
      { 
        type: 'separator', 
        requiredPermissions: ['router.scan'],
        whitelist: getWhitelistFromPermissions(['router.scan']),
      },
      { 
        type: 'action', 
        label: 'Job Scanner', 
        action: () => handleNavigation('/scanner'), 
        associatedPath: '/scanner', 
        requiredPermissions: ['router.scan'],
        whitelist: getWhitelistFromPermissions(['router.scan']),
      },
    ],
    activePathPrefixes: ['/schedule', '/router', '/shipments', '/scanner'],
  };

  const qaMenu: MenuCategory = {
    trigger: 'Quality',
    items: [
      { 
        type: 'action', 
        label: 'NC Report', 
        action: () => handleNavigation('/qa/tickets'), 
        associatedPath: '/qa/tickets', 
        requiredPermissions: ['qa.view'],
        whitelist: getWhitelistFromPermissions(['qa.view']),
      },
      { 
        type: 'action', 
        label: 'NC Scanner', 
        action: () => handleNavigation('/qa/scanner'), 
        associatedPath: '/nc/scanner', 
        requiredPermissions: ['qa.view', 'router.scan'],
        whitelist: getWhitelistFromPermissions(['qa.view', 'router.scan']),
      },
      { 
        type: 'action', 
        label: 'Exception Codes', 
        action: () => handleNavigation('/qa/exceptions'), 
        associatedPath: '/qa/exceptions', 
        requiredPermissions: ['qa.exceptions'],
        whitelist: getWhitelistFromPermissions(['qa.exceptions']),
      },
    ],
    activePathPrefixes: ['/qa'],
  };

  const settingsMenu: MenuCategory = {
    trigger: 'Admin',
    items: [
      { 
        type: 'action', 
        label: 'Reports', 
        action: () => handleNavigation('/reports'), 
        associatedPath: '/reports', 
        requiredPermissions: ['admin.reports'],
        whitelist: getWhitelistFromPermissions(['admin.reports']),
      },
      { 
        type: 'action', 
        label: 'Accounting Periods', 
        action: () => handleNavigation('/admin/accounting-periods'), 
        associatedPath: '/admin/accounting-periods', 
        requiredPermissions: ['admin.period-closing'],
        whitelist: getWhitelistFromPermissions(['admin.period-closing']),
      },
      { 
        type: 'action', 
        label: 'Utilities', 
        action: () => handleNavigation('/util/parsers'), 
        associatedPath: '/util/parsers', 
        requiredPermissions: ['admin.utilities'],
        whitelist: getWhitelistFromPermissions(['admin.utilities']),
      },
      { 
        type: 'action', 
        label: 'Vacation Calendar', 
        action: () => handleNavigation('/util/vacation-calendar'), 
        associatedPath: '/util/vacation-calendar', 
        requiredPermissions: ['admin.vacation-calendar'],
        whitelist: getWhitelistFromPermissions(['admin.vacation-calendar']),
      },
      { type: 'action', label: 'Settings', action: toggleSettingsCenter, whitelist: [] as string[] },
      { type: 'action', label: 'Clear Cache', action: handleClearCache, whitelist: [] as string[] },
      { type: 'action', label: 'Sign out', action: onLogout, whitelist: [] as string[] },
    ],
    activePathPrefixes: ['/admin', '/reports'],
  };

  return [
    salesMenu,
    productionMenu,
    productDevelopmentMenu,
    materialsMenu,
    qaMenu,
    settingsMenu,
  ];
};
